// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/*
 * Click-to-define. The audience knows "prompt" and "seed" but not necessarily
 * "ancillary chunk", and a tool that explains its own vocabulary in place is
 * more trustworthy than one that assumes.
 */

export const GLOSSARY: Record<string, [string, string]> = {
  chunk: [
    'Chunk',
    'A PNG is a signature followed by a run of chunks. Each is a length, a four-letter type, the data, and a checksum. Text chunks are ordinary chunks that carry words instead of pixels, which is where a generation recipe usually lives.',
  ],
  idat: [
    'IDAT',
    'The chunks holding the compressed picture itself. Many readers stop as soon as they have the image, which is why a text chunk written after IDAT — perfectly legal — is so often missed.',
  ],
  text: [
    'tEXt, zTXt, iTXt',
    'The three standard ways a PNG carries text. tEXt is plain, zTXt is compressed, iTXt is compressed-or-not and allows any language. Some readers only look for one of the three.',
  ],
  crc: [
    'Checksum',
    'Every chunk carries a CRC-32 of its own bytes. If one byte is damaged in transit the checksum stops matching. Strict readers throw the whole file away at that point — including the intact text sitting next to the damage.',
  ],
  lsb: [
    'The pixel plane',
    'The lowest bit of each colour or alpha sample, which can be flipped without any visible change. NovelAI writes its generation settings there by default, and Forge can too. No chunk reader can see it, because it is not in a chunk — it is in the picture.',
  ],
  graft: [
    'Graft',
    'Writing a recipe back into an image as text chunks, placed immediately after the header so any reader reaches them. The picture data is copied across byte for byte and never re-encoded.',
  ],
  sidecar: [
    'Sidecar',
    'A recipe saved as its own small file next to the image — a .json or .txt. Useful when the image has to travel through something that strips chunks: keep the sidecar, and graft it back afterwards.',
  ],
  workflow: [
    'Workflow and prompt',
    'ComfyUI saves two graphs. "workflow" is the node canvas you edited; "prompt" is the flattened form actually executed. Dragging an image in wants "workflow"; ComfyUI can rebuild a canvas from "prompt" alone if that is all there is.',
  ],
};

let tip: HTMLElement | null = null;

function hide() {
  tip?.remove();
  tip = null;
}

function show(anchor: HTMLElement, term: string) {
  hide();
  const entry = GLOSSARY[term];
  if (!entry) return;

  const el = document.createElement('div');
  el.id = 'glossary-tip';
  el.setAttribute('role', 'tooltip');
  const strong = document.createElement('strong');
  strong.textContent = entry[0];
  el.append(strong, document.createTextNode(entry[1]));
  document.body.append(el);

  const rect = anchor.getBoundingClientRect();
  const box = el.getBoundingClientRect();
  const margin = 8;
  let left = rect.left;
  if (left + box.width > window.innerWidth - margin) left = window.innerWidth - box.width - margin;
  if (left < margin) left = margin;
  let top = rect.bottom + 6;
  if (top + box.height > window.innerHeight - margin) top = Math.max(margin, rect.top - box.height - 6);
  el.style.left = `${left}px`;
  el.style.top = `${top}px`;
  tip = el;
}

/** Wraps a term for use inside an innerHTML string. */
export function term(key: string, label?: string): string {
  const text = label ?? key;
  if (!GLOSSARY[key]) return text;
  return `<span class="glossary-link" data-term="${key}" role="button" tabindex="0">${text}</span>`;
}

export function initGlossary() {
  document.addEventListener('click', (event) => {
    const target = (event.target as HTMLElement | null)?.closest<HTMLElement>('.glossary-link');
    if (!target) {
      if (tip && !tip.contains(event.target as Node)) hide();
      return;
    }
    event.preventDefault();
    const key = target.dataset.term;
    if (key) show(target, key);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && tip) {
      // Only swallow the key when a tooltip is actually up, so Escape still
      // reaches whatever overlay is behind it.
      event.stopPropagation();
      hide();
      return;
    }
    const target = event.target as HTMLElement | null;
    if ((event.key === 'Enter' || event.key === ' ') && target?.classList.contains('glossary-link')) {
      event.preventDefault();
      const key = target.dataset.term;
      if (key) show(target, key);
    }
  });

  window.addEventListener('scroll', hide, { passive: true });
  window.addEventListener('resize', hide);
}
